import React, { useState, useEffect } from 'react';
import api from '../api/axiosConfig';
import './QuestionBank.css';
import Modal from './Modal';
import QuestionEditor from './QuestionEditor';
import { ImageIcon } from 'lucide-react';
import ImagePreview from './ImagePreview';
import AdminJsonUploadMini from './AdminJsonUploadMini';

const QuestionBank = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [data, setData] = useState(
    {
      grades: [],
      subjects: [],
      topics: [],
      questions: [],
      selectedGrade: localStorage.getItem('qb-lastGrade') || '',
      selectedSubject: localStorage.getItem('qb-lastSubject') || '',
      selectedTopic: localStorage.getItem('qb-lastTopic') || ''
    }
  );

  const [previewImage, setPreviewImage] = useState(null);
  const [jsonUploading, setJsonUploading] = useState(false);
  const [jsonUploadStatus, setJsonUploadStatus] = useState('idle');
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    await fetchGrades();
  };

  useEffect(() => {
    if (data.selectedGrade) {
      fetchSubjects(data.selectedGrade);
    }
  }, [data.selectedGrade]);

  useEffect(() => {
    if (data.selectedSubject) {
      fetchTopics(data.selectedSubject);
    }
  }, [data.selectedSubject]);

  useEffect(() => {
    if (data.selectedTopic) {
      fetchQuestions(data.selectedTopic);
    }
  }, [data.selectedTopic]);

  const fetchGrades = async () => {
    const res = await api.get('/admin/grades');
    setData(prev => ({ ...prev, grades: res.data }));
  };

  const fetchSubjects = async (gradeId) => {
    const res = await api.get('/admin/subjects?gradeId=' + gradeId);
    setData(prev => ({ ...prev, subjects: res.data }));
  };

  const fetchTopics = async (subjectId) => {
    const res = await api.get('/admin/topics?subjectId=' + subjectId);
    setData(prev => ({ ...prev, topics: res.data }));
  };

  const fetchQuestions = async (topicId) => {
    const res = await api.get('/admin/questions?topicId=' + topicId);
    setData(prev => ({ ...prev, questions: res.data }));
  };

  const onSubjectSelected = (subject) => {
    localStorage.setItem('qb-lastSubject', subject);
    localStorage.removeItem('qb-lastTopic');
    setData(prev => ({ ...prev, selectedTopic: '', selectedSubject: subject, questions: [] }));
  };

  const onTopicSelected = (topic) => {
    localStorage.setItem('qb-lastTopic', topic);
    setData({ ...data, selectedTopic: topic, questions: [] });
  };

  const onGradeSelected = async (grade) => {
    localStorage.setItem('qb-lastGrade', grade);
    localStorage.removeItem('qb-lastSubject');
    localStorage.removeItem('qb-lastTopic');
    setData(
      {
        ...data,
        questions: [],
        subjects: [],
        topics: [],
        selectedSubject: '',
        selectedTopic: '',
        selectedGrade: grade
      });
  };

  const handleUpdateSuccess = () => {
    setIsModalOpen(false);
    if (data.selectedTopic) {
      fetchQuestions(data.selectedTopic);
    }
  };

  const handleEditClick = (question) => {
    setEditingQuestion(question);
    setIsModalOpen(true);
  };

const handleUploadJSON = async (jsonData, error) => {
    // Check if front-end parsing failed or wrong file attached
    if (error) {
      setJsonUploadStatus('error');
      console.error("File processing error:", error.message);
      return;
    }

    // 1. Kick off loader indicators
    setJsonUploading(true);
    setJsonUploadStatus('idle');

    try {
      // Get your auth token (assuming it's stored in localStorage for your 'protect' middleware)
      const token = localStorage.getItem('token'); 

      // 2. Make the API Call using Axios
      const response = await api.post(
        '/admin/questions/upload-json', 
        {
          topicId: data.selectedTopic,
          questions: jsonData // This is the questions array array sent out from the mini uploader
        },
        {
          headers: {
            'Content-Type': 'application/json',
            // Include auth header if your 'protect' middleware requires it:
            ...(token && { 'Authorization': `Bearer ${token}` }) 
          }
        }
      );

      // Axios automatically throws errors for non-2xx statuses, so if we reach here, it worked!
      setJsonUploadStatus('success');
      
      // 3. Automatically flash success, then reset the component state back to default
      setTimeout(() => {
        setJsonUploadStatus('idle');
      }, 3000);

    } catch (apiError) {
      setJsonUploadStatus('error');
      console.error('API Error Response:', apiError.response?.data || apiError.message);
    } finally {
      setJsonUploading(false);
    }
  };

  return (
    <div className="bank-container">
      <div className="page-header">
        <h2>Question Bank</h2>
        <AdminJsonUploadMini onUpload={handleUploadJSON}
          isUploading={jsonUploading}
          disabled={!data.selectedTopic}
          uploadStatus={jsonUploadStatus}
          onResetStatus={() => {
            setJsonUploadStatus('idle');
          }}
        />
      </div>

      {/* FILTER BAR */}
      <div className="filter-bar">
        <input
          type="text"
          placeholder="Search question text..."
          className="search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={"Editing Question"}>
          {editingQuestion && (
            <QuestionEditor
              question={editingQuestion}
              onSave={handleUpdateSuccess}
              onCancel={() => setIsModalOpen(false)}
            />
          )}
        </Modal>

        <Modal isOpen={previewImage} onClose={() => setPreviewImage(null)} title={"Preview"}>
          {previewImage && (
            <ImagePreview
              imageUrl={previewImage}
              onCancel={() => setPreviewImage(null)}
            />
          )}
        </Modal>

        <select value={data.selectedGrade} onChange={(e) => { onGradeSelected(e.target.value) }}>
          <option value="">Select Grade</option>
          {data.grades.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>

        <select
          value={data.selectedSubject}
          onChange={(e) => { onSubjectSelected(e.target.value) }}
          disabled={!data.selectedGrade}
        >
          <option value="">Select Subject</option>
          {data.subjects.map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>

        <select
          value={data.selectedTopic}
          onChange={(e) => { onTopicSelected(e.target.value) }}
          disabled={!data.selectedSubject}
        >
          <option value="">Select Topic</option>
          {data.topics.map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </div>

      {/* TABLE */}
      <div className="bank-table-wrapper">
        <table className="bank-table">
          <thead>
            <tr>
              <th>SL</th>
              <th>Type</th>
              <th>Question Content</th>
              <th>Points</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.questions.map((q, index) => (
              <tr key={q.id}>
                <td><span className={`type-badge`}>{index + 1}</span></td>
                <td><span className={`type-badge ${q.type}`}>{q.type}</span></td>
                <td className="question-cell" style={{ padding: 0 }}>
                  <div className="cell-flex-layout" style={{ display: 'flex', alignItems: 'stretch', width: '100%' }}>

                    {/* Left Side Icon Container - Stretches vertically to full height */}
                    <div
                      className="icon-column"
                      onClick={() => q.imageUrl && setPreviewImage(q.imageUrl)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0 12px',
                        flexShrink: 0
                      }}
                    >
                      <ImageIcon
                        size={18}
                        style={{
                          color: q.imageUrl ? '#3b82f6' : '#d1d5db', // Highlighted blue if active, grayed out if no image
                          opacity: q.imageUrl ? 1 : 0.4
                        }}
                        title={q.imageUrl ? "This question includes an image illustration" : "No image attached"}
                      />
                    </div>

                    {/* Right Side Content Area */}
                    <div className="content-column" style={{ padding: '12px 16px', flexGrow: 1 }}>
                      <strong>{q.text}</strong>

                      {q.type === 'SHORT' ? (
                        <div className="ans-preview" style={{ marginTop: '4px' }}>
                          Ans: {q.correctAnswerText}
                        </div>
                      ) : (
                        <div className="ans-preview" style={{ marginTop: '4px' }}>
                          {q.Options?.length || 0} Options (Correct: {q.Options?.find(o => o.isCorrect)?.text || 'None'})
                        </div>
                      )}
                    </div>

                  </div>
                </td>
                <td>{q.points}</td>
                <td>
                  <button
                    className="btn-primary"
                    onClick={() => handleEditClick(q)}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default QuestionBank;